import { Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { Menu, X, Search } from "lucide-react";
import { siteConfig, categorySlug } from "@/config";

const primaryNav = [
  { label: "Home", to: "/" },
  { label: "About", to: "/about-us" },
  { label: "Editorial", to: "/editorial-policy" },
  { label: "Contact", to: "/contact-us" },
];

export function Header() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  return (
    <header className="sticky top-0 z-40 border-b border-border bg-background/90 backdrop-blur">
      <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-4">
        <Link to="/" className="flex items-center gap-2">
          <span
            aria-hidden="true"
            className="grid h-8 w-8 place-items-center rounded-md bg-primary text-primary-foreground font-bold"
          >
            G
          </span>
          <span className="text-lg font-bold tracking-tight">
            {siteConfig.siteName}
          </span>
        </Link>

        <nav className="hidden items-center gap-6 md:flex">
          {primaryNav.map((n) => (
            <Link
              key={n.to}
              to={n.to}
              className="text-sm font-medium text-muted-foreground hover:text-foreground"
              activeProps={{ className: "text-foreground" }}
            >
              {n.label}
            </Link>
          ))}
          <button
            type="button"
            aria-label="Search"
            onClick={() => navigate({ to: "/search" })}
            className="rounded-md p-2 hover:bg-muted"
          >
            <Search className="h-4 w-4" />
          </button>
        </nav>

        <button
          type="button"
          className="md:hidden rounded-md p-2 hover:bg-muted"
          aria-label={open ? "Close menu" : "Open menu"}
          onClick={() => setOpen((v) => !v)}
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {/* Secondary category bar */}
      <div className="hidden border-t border-border bg-background md:block">
        <div className="mx-auto flex max-w-6xl items-center gap-4 overflow-x-auto px-4 py-2 text-sm">
          <span className="shrink-0 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Topics
          </span>
          {siteConfig.categories.map((c) => (
            <Link
              key={c}
              to="/category/$category"
              params={{ category: categorySlug(c) }}
              className="shrink-0 rounded-full border border-border px-3 py-1 text-muted-foreground hover:border-primary hover:text-foreground"
              activeProps={{ className: "border-primary text-foreground" }}
            >
              {c}
            </Link>
          ))}
        </div>
      </div>

      {open && (
        <div className="border-t border-border bg-background md:hidden">
          <nav className="mx-auto flex max-w-6xl flex-col gap-1 px-4 py-3">
            {primaryNav.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                onClick={() => setOpen(false)}
                className="rounded-md px-2 py-2 text-sm font-medium hover:bg-muted"
              >
                {n.label}
              </Link>
            ))}
            <div className="mt-2 border-t border-border pt-2">
              <p className="px-2 pb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Topics
              </p>
              {siteConfig.categories.map((c) => (
                <Link
                  key={c}
                  to="/category/$category"
                  params={{ category: categorySlug(c) }}
                  onClick={() => setOpen(false)}
                  className="block rounded-md px-2 py-2 text-sm hover:bg-muted"
                >
                  {c}
                </Link>
              ))}
            </div>
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                navigate({ to: "/search" });
              }}
              className="mt-2 flex items-center gap-2 rounded-md px-2 py-2 text-left text-sm font-medium hover:bg-muted"
            >
              <Search className="h-4 w-4" /> Search
            </button>
          </nav>
        </div>
      )}
    </header>
  );
}
